/**
 * SimOverrideBanner.tsx — shared banner shown on every page that consumes
 * simulation-data overrides from the Simulation Data Hub.
 *
 * Lists which override IDs are currently active on this page and provides
 * a link back to /simulation-data so the user can edit them.
 */

import { useNavigate } from 'react-router';
import { Database, CheckCircle2, ExternalLink } from 'lucide-react';

interface Props {
  /** Per-page list of override IDs the page actually consumes */
  consumes: Array<{ id: string; label: string; active: boolean; value?: string }>;
}

export default function SimOverrideBanner({ consumes }: Props) {
  const navigate = useNavigate();
  const activeCount = consumes.filter(c => c.active).length;
  if (activeCount === 0) {
    return (
      <div className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-xs flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-slate-400">
          <Database className="w-3.5 h-3.5" />
          <span>No simulation-data overrides applied on this page. Defaults from literature in use.</span>
        </div>
        <button onClick={() => navigate('/simulation-data')}
          className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200">
          Open data hub <ExternalLink className="w-3 h-3" />
        </button>
      </div>
    );
  }
  return (
    <div className="rounded-xl border-2 border-violet-700 bg-violet-900/15 px-4 py-2 text-xs">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-violet-200 font-semibold">
          <Database className="w-3.5 h-3.5" />
          <span>{activeCount} simulation-data override{activeCount === 1 ? '' : 's'} active on this page</span>
        </div>
        <button onClick={() => navigate('/simulation-data')}
          className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200">
          Edit values <ExternalLink className="w-3 h-3" />
        </button>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {consumes.map(c => c.active ? (
          <span key={c.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-900/30 text-emerald-200 text-[10px] border border-emerald-700/40">
            <CheckCircle2 className="w-2.5 h-2.5" /> {c.label}: <strong>{c.value}</strong>
          </span>
        ) : (
          <span key={c.id} className="inline-flex items-center px-2 py-0.5 rounded bg-slate-800 text-slate-500 text-[10px] border border-slate-700">
            {c.label}: <span className="ml-1 italic">default</span>
          </span>
        ))}
      </div>
    </div>
  );
}
